import {useId, useState} from "react";
import {InputAdornment, Menu, MenuItem, useTheme} from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BaseInput from "components/BaseInput";
import {MenuItemLabel, rowMenuItemSx, rowMenuPaperSlotProps} from "components/rowMenu/rowMenu.styles";
import {useMessages} from "i18n";
import {COMPANY_ID_MAX, COMPANY_NAME_MAX, type SenderChoice} from "sender/senderChoice";
import type {SenderChoiceControls} from "./useSenderChoice";
import {CompanyFields, FieldNote, SenderKindButton} from "./PersonaliseSenderFields.styles";

type Props = {
	controls: SenderChoiceControls;
	/** The look's name, which a transfer or request sent as a person is named by. */
	name: string;
	onNameChange: (name: string) => void;
	disabled: boolean;
};

const KIND_ICONS = {person: PersonOutlinedIcon, company: BusinessOutlinedIcon} as const;

/**
 * "Your name" in the home panel's Personalise builder, whether it sends files or asks for them
 * (kitecorp/transferify-ui#3140, #3186, owners 2026-10-01 and 2026-10-02): the button at its right edge chooses who
 * the transfer or request goes out as. Person keeps the name field, the look's name; Company puts the company's name
 * and tax ID in its place. The copy is the file request's, which shipped first and says nothing request-specific. A
 * dumb view over {@link SenderChoiceControls}, which the panel holds, so the choice outlives the builder closing.
 */
export function PersonaliseSenderFields({controls, name, onNameChange, disabled}: Props) {
	const {sender: senderCopy, reminders} = useMessages();
	const copy = reminders.create.sendAs;
	const options = reminders.options;
	const {tokens} = useTheme();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const menuId = useId();
	const noteId = useId();
	const {sender, companyIdInvalid} = controls;
	const KindIcon = KIND_ICONS[sender.kind];
	const choose = (kind: SenderChoice["kind"]) => {
		controls.choose(kind);
		setMenuAnchor(null);
	};

	const kindButton = (
		<InputAdornment position="end">
			<SenderKindButton
				type="button"
				disabled={disabled}
				aria-label={copy.aria(copy[sender.kind])}
				aria-haspopup="menu"
				aria-expanded={menuAnchor !== null}
				aria-controls={menuAnchor === null ? undefined : menuId}
				onClick={event => setMenuAnchor(event.currentTarget)}
			>
				<KindIcon sx={{fontSize: 20}} aria-hidden="true"/>
				<ArrowDropDownIcon sx={{fontSize: 20}} aria-hidden="true"/>
			</SenderKindButton>
		</InputAdornment>
	);

	return (
		<>
			{sender.kind === "person" ? (
				<BaseInput
					label={senderCopy.personalise.nameLabel}
					placeholder={senderCopy.personalise.namePlaceholder}
					value={name}
					disabled={disabled}
					onChange={event => onNameChange(event.target.value)}
					endAdornment={kindButton}
				/>
			) : (
				<CompanyFields>
					<BaseInput
						label={options.companyNameLabel}
						required
						value={sender.company.name}
						disabled={disabled}
						onChange={event => controls.setCompany({...sender.company, name: event.target.value})}
						inputProps={{autoComplete: "organization", maxLength: COMPANY_NAME_MAX}}
						endAdornment={kindButton}
					/>
					<div>
						<BaseInput
							label={options.companyIdLabel}
							value={sender.company.id}
							disabled={disabled}
							error={companyIdInvalid}
							onChange={event => controls.setCompany({...sender.company, id: event.target.value})}
							inputProps={{"aria-describedby": noteId}}
						/>
						<FieldNote id={noteId} $error={companyIdInvalid}>
							{companyIdInvalid ? options.companyIdInvalid(COMPANY_ID_MAX) : options.companyIdHint}
						</FieldNote>
					</div>
				</CompanyFields>
			)}
			<Menu
				id={menuId}
				open={menuAnchor !== null}
				anchorEl={menuAnchor}
				onClose={() => setMenuAnchor(null)}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				slotProps={rowMenuPaperSlotProps(tokens.cardBorder)}
			>
				{(["person", "company"] as const).map(kind => {
					const Icon = KIND_ICONS[kind];
					return (
						<MenuItem
							key={kind}
							role="menuitemradio"
							aria-checked={sender.kind === kind}
							selected={sender.kind === kind}
							sx={rowMenuItemSx}
							onClick={() => choose(kind)}
						>
							<MenuItemLabel><Icon sx={{fontSize: 18}} aria-hidden="true"/>{copy[kind]}</MenuItemLabel>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
}
